import { FolderIcon, TrashSimpleIcon, WarningCircleIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { useId, useState } from 'react'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { useDeleteProject } from '../../utils/api-client/api-hooks.ts'
import type { ProjectResponse } from '../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { TextInput } from '../input/text-input.tsx'
import { ADMIN_REQUIRED_OVERRIDE } from './admin-required-override.ts'
import * as styles from './confirm-dialog.css.ts'

export type DeleteProjectDialogProps = {
  project: Pick<ProjectResponse, 'id' | 'name' | 'repository_count' | 'feedback_count'>
  onClose: () => unknown
  onDeleted: () => unknown
}

/** Confirms deleting a project: its project-scoped feedback goes with it, its repositories stay connected. */
export const DeleteProjectDialog: React.FC<DeleteProjectDialogProps> = ({ project, onClose, onDeleted }) => {
  const { addToast } = useToastService()
  const [typedName, setTypedName] = useState('')
  const confirmInputId = useId()
  const confirmHintId = `${confirmInputId}-hint`
  const showError = useShowMutationError({ prefix: 'Failed to delete project', overrides: ADMIN_REQUIRED_OVERRIDE })
  const deleteMutation = useDeleteProject({
    onSuccess: () => {
      addToast({ message: <>Project &ldquo;{project.name}&rdquo; deleted</>, variant: 'success' })
      onDeleted()
    },
    onError: showError,
  })
  const isPending = deleteMutation.isPending
  const count = project.repository_count
  const feedbackCount = project.feedback_count ?? 0

  // The spec's confirmation body leads with how much project-scoped feedback the delete removes and
  // how many repositories it ungroups; the feedback clause is omitted when there is none, the
  // repositories clause when there are none.
  const clauses: string[] = []
  if (feedbackCount > 0) {
    clauses.push(
      `${feedbackCount} ${pluralize(feedbackCount, 'feedback item', 'feedback items')} left at this project's scope ${pluralize(feedbackCount, 'is', 'are')} deleted with it`
    )
  }
  if (count > 0) {
    clauses.push(`its ${count} ${pluralize(count, 'repository becomes', 'repositories become')} ungrouped`)
  }
  const summary = clauses.join(', and ')
  const summarySentence = summary ? summary.charAt(0).toUpperCase() + summary.slice(1) : ''

  // The destructive act is gated on retyping the name: the reader has to have read which project
  // this is before the button will fire.
  const isNameConfirmed = typedName === project.name
  const isDeleteDisabled = !isNameConfirmed || isPending

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open && !isPending) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content} onPointerDownOutside={event => event.preventDefault()}>
          <Dialog.Title className={styles.title}>
            <WarningCircleIcon size={32} weight="fill" className={styles.destructiveIcon} aria-hidden />
            Delete project?
          </Dialog.Title>
          <div className={styles.subjectCard}>
            <span className={styles.subjectKind}>
              <FolderIcon size={20} weight="light" aria-hidden />
              <span>Project</span>
            </span>
            <p className={styles.subjectName}>{project.name}</p>
          </div>
          <Dialog.Description asChild>
            <p className={styles.description}>
              {summarySentence && <>{summarySentence}. </>}
              {count > 0 && <>The {pluralize(count, 'repository stays', 'repositories stay')} connected to Pixee. </>}
              This cannot be undone.
            </p>
          </Dialog.Description>
          <TextInput
            size="small"
            id={confirmInputId}
            label={`Type ${project.name} to confirm`}
            value={typedName}
            onChange={setTypedName}
            placeholder={project.name}
            autoComplete="off"
            aria-describedby={confirmHintId}
          />
          {/* Announces why the button is unavailable; `aria-disabled` on the button alone says that it
              is, not what to do about it. */}
          <p className={styles.confirmHint} id={confirmHintId} role="status">
            {isNameConfirmed ? 'Delete project is available.' : 'Delete project is unavailable until the name matches.'}
          </p>
          <div className={styles.buttons}>
            <GhostButton
              type="secondary"
              size="small"
              className={styles.outlinedGhost}
              onClick={onClose}
              state={isPending ? 'disabled' : 'default'}
            >
              Cancel
            </GhostButton>
            <DefaultButton
              buttonType="destructive"
              size="small"
              state={isPending ? 'loading' : isDeleteDisabled ? 'disabled' : 'default'}
              onClick={() => deleteMutation.mutate({ projectId: project.id })}
              loadingLabel="Deleting project"
              icon={<TrashSimpleIcon size={14} weight="light" aria-hidden />}
            >
              Delete project
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
